import { API_ENDPOINT } from '../services/api';
import { getScaledValue } from '../ui/styled';
import branding from '../../config/branding';

const profilesTitles: Record<string, string> = {
  male: 'Masculino',
  female: 'Feminino',
};

export const parseStrapiImage = (url: string | null | undefined) => {
  return url
    ? url?.indexOf('://') !== -1
      ? url
      : url?.indexOf(branding.cdnDomain) !== -1
        ? `https://${url}`
        : `${API_ENDPOINT.replace(/\/api$/, '')}${url}`
    : '';
};

export const parseProfile = (item: Record<string, unknown>) => {
  return {
    ...item,
    id: item.id,
    name: item.name,
    image: { uri: parseStrapiImage((item?.picture as Record<string, string>)?.url) },
    gender: { title: profilesTitles[item.gender as string], type: item.gender },
  };
};

export const parseProfileImage = (item: Record<string, unknown>) => {
  return {
    id: item.id,
    name: item.name,
    image: { uri: parseStrapiImage(item.url as string) },
  };
};

const categoriesOption: Record<string, { title: string; color: string }> = {
  Filme: {
    title: 'Filme',
    color: 'blue',
  },
  Série: {
    title: 'Série',
    color: 'yellow',
  },

  Musical: {
    title: 'Musical',
    color: 'blue',
  },
  Educativo: {
    title: 'Educativo',
    color: 'green',
  },
};

export const parseBanner = (item: Record<string, unknown>) => {
  const featuredShow = item?.featured_show as Record<string, unknown> | undefined;
  const showData = featuredShow?.data as Record<string, unknown> | undefined;
  const showAttrs = showData?.attributes as Record<string, unknown> | undefined;
  const featuredBanner = item?.featured_banner as Record<string, unknown> | undefined;
  const bannerData = featuredBanner?.data as Record<string, unknown> | undefined;
  const bannerAttrs = bannerData?.attributes as Record<string, unknown> | undefined;
  return {
    id: showData?.id,
    title: showAttrs?.name,
    description: showAttrs?.description,
    image: { uri: parseStrapiImage(bannerAttrs?.url as string | undefined) },
    category: categoriesOption['Filme'],
  };
};

interface IconDef {
  icon: string;
  width: number;
  height: number;
  fill: string;
}

const categoryIcons: Record<string, IconDef> = {
  Playlist: { icon: 'play', width: getScaledValue(9), height: getScaledValue(11), fill: '#ffffff' },
  Conteúdos: {
    icon: 'smile',
    width: getScaledValue(15),
    height: getScaledValue(17),
    fill: branding.colors.primary,
  },
  Educativos: {
    icon: 'paint',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: branding.colors.primary,
  },
  Filmes: {
    icon: 'popcorn',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: branding.colors.primary,
  },
  'Ver Mais': {
    icon: 'plus',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: '#ffffff',
  },
};

export const parseCategory = (item: Record<string, unknown>) => {
  const attrs = item?.attributes as Record<string, unknown> | undefined;
  const iconData = (attrs?.icon as Record<string, unknown>)?.data as
    | Record<string, unknown>
    | undefined;
  const iconAttrs = iconData?.attributes as Record<string, unknown> | undefined;
  return {
    title: attrs?.name,
    id: item?.id,
    icon: categoryIcons[attrs?.name as string] || {},
    image: iconAttrs?.url ? parseStrapiImage(iconAttrs.url as string) : null,
  };
};

const collectionResources: Record<string, Record<string, unknown>> = {
  'Meus Favoritos': {
    icon: {
      icon: 'liked',
      width: getScaledValue(22),
      height: getScaledValue(20),
      fill: branding.colors.primary,
    },
  },
  'As 20 Músicas + Vistas': {
    icon: {
      icon: 'music',
      width: getScaledValue(17),
      height: getScaledValue(21),
      fill: branding.colors.primary,
    },
  },
  'Cante e Dance': {
    icon: {
      icon: 'music',
      width: getScaledValue(17),
      height: getScaledValue(21),
      fill: branding.colors.primary,
    },
  },
};

export const parseCollection = (item: Record<string, unknown>) => {
  const attrs = item?.attributes as Record<string, unknown> | undefined;
  const streamShows = attrs?.stream_shows as Record<string, unknown> | undefined;
  const showsData = streamShows?.data as Record<string, unknown>[] | undefined;
  const iconData = (attrs?.icon as Record<string, unknown>)?.data as
    | Record<string, unknown>
    | undefined;
  const iconAttrs = iconData?.attributes as Record<string, unknown> | undefined;
  return {
    id: item?.id,
    title: attrs?.name,
    ...(collectionResources[attrs?.name as string] || {}),
    videos: showsData?.length ? showsData.map(parseVideo) : [],
    image: iconAttrs?.url ? parseStrapiImage(iconAttrs.url as string) : null,
  };
};

export const parseVideo = (item: Record<string, unknown>) => {
  const attrs = item?.attributes as Record<string, unknown> | undefined;
  const thumbnail = attrs?.thumbnail as Record<string, unknown> | undefined;
  const thumbData = thumbnail?.data as Record<string, unknown> | undefined;
  const thumbAttrs = thumbData?.attributes as Record<string, unknown> | undefined;
  const flatThumb = item?.thumbnail as Record<string, unknown> | undefined;
  const flatThumbData = flatThumb?.data as Record<string, unknown> | undefined;
  const flatThumbAttrs = flatThumbData?.attributes as Record<string, unknown> | undefined;
  return {
    id: item?.id,
    title: attrs?.name || item?.name,
    description: attrs?.description || item?.description,
    cover: {
      uri: parseStrapiImage(
        (thumbAttrs?.url as string) ||
          (flatThumbAttrs?.url as string) ||
          (flatThumb?.url as string),
      ),
    },
  };
};

export const parseVideoShow = (item: Record<string, unknown>) => {
  const attrs = item?.attributes as Record<string, unknown> | undefined;
  const widePic = attrs?.wide_picture as Record<string, unknown> | undefined;
  const widePicData = widePic?.data as Record<string, unknown> | undefined;
  const widePicAttrs = widePicData?.attributes as Record<string, unknown> | undefined;
  const flatWidePic = item?.wide_picture as Record<string, unknown> | undefined;
  const flatWidePicData = flatWidePic?.data as Record<string, unknown> | undefined;
  const flatWidePicAttrs = flatWidePicData?.attributes as Record<string, unknown> | undefined;
  const thumb = attrs?.thumbnail as Record<string, unknown> | undefined;
  const thumbData = thumb?.data as Record<string, unknown> | undefined;
  const thumbAttrs = thumbData?.attributes as Record<string, unknown> | undefined;
  const flatThumb = item?.thumbnail as Record<string, unknown> | undefined;
  const flatThumbData = flatThumb?.data as Record<string, unknown> | undefined;
  const flatThumbAttrs = flatThumbData?.attributes as Record<string, unknown> | undefined;
  return {
    ...item,
    title: item.name,
    cover: {
      uri: parseStrapiImage(
        (widePicAttrs?.url as string) ||
          (flatWidePicAttrs?.url as string) ||
          (flatWidePic?.url as string),
      ),
    },
    thumb: {
      uri: parseStrapiImage(
        (thumbAttrs?.url as string) ||
          (flatThumbAttrs?.url as string) ||
          (flatThumb?.url as string),
      ),
    },
  };
};
